import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react'
import { api, AuthorizationError } from '@/api/client'
import type { SecurityDepositAutomation } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'

/**
 * The global security-deposit kill switch.
 *
 * Deliberately asymmetric: arming asks for confirmation because it starts real
 * holds on real guest cards, while disarming is a single click with no dialog
 * in the way. Disarm is the emergency stop, and an emergency stop that makes
 * you confirm first is the wrong shape.
 *
 * Backed by a Durable Object rather than a deploy-time var, so both directions
 * take effect on the next sweep with no deploy. See admin-api's
 * docs/uplisting-security-deposit-plan.md.
 */

/** How long to wait on either call before giving up and re-enabling the UI. */
const REQUEST_TIMEOUT_MS = 10_000

/**
 * Run a request with a bounded lifetime.
 *
 * Both directions need this, not just the read. A server that accepts the
 * connection and then hangs would otherwise leave the read spinning forever
 * (never reaching the error state, which is the one that offers Disarm) or
 * leave the mutation pending forever (which disables every action, so the
 * operator cannot retry the safe, idempotent disarm without reloading).
 *
 * Built on setTimeout rather than `AbortSignal.timeout`, which is implemented
 * natively and so ignores test fake timers - meaning the timeout path could
 * never be covered by a fast test.
 */
async function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, outer?: AbortSignal) {
  const controller = new AbortController()
  const abort = () => controller.abort()
  outer?.addEventListener('abort', abort)
  const timer = setTimeout(
    () => controller.abort(new Error('The request timed out')),
    REQUEST_TIMEOUT_MS
  )
  try {
    return await run(controller.signal)
  } finally {
    clearTimeout(timer)
    outer?.removeEventListener('abort', abort)
  }
}

/** What a write to the switch carries. Declared once so the mutation's generics
 *  are explicit - inference picked them up from a callback signature instead,
 *  and quietly widened the result to `unknown`. */
interface AutomationWriteVars {
  enabled: boolean
  reason?: string
  ifVersion?: number
}

export function SecurityDepositAutomationCard() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  /**
   * The version in force when the arm dialog opened.
   *
   * Sent as `if_version` instead of whatever the latest poll rendered. Reading
   * the live value would silently adopt another operator's accepted write: open
   * at V, a poll observes their change at V+1, and confirming would send V+1 -
   * sailing through the server's conflict check and re-arming after their
   * disarm. The whole point of the check is that this confirmation is stale.
   */
  const [armVersionAtOpen, setArmVersionAtOpen] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  // When a write fails we no longer know the switch's real state: aborting a
  // fetch does not undo a write the server may already have committed. See
  // `stateUncertain` below.
  /**
   * The version an abandoned ARM was based on, or null.
   *
   * This is the conclusive resolution signal, replacing an earlier wall-clock
   * window. That window was arbitrary: nothing bounds how long a request the
   * client gave up on may sit queued server-side, so letting it expire could
   * drop the warning while the arm was still able to commit.
   *
   * The version is exact. The abandoned arm carries `if_version: V`, and the
   * server declines any arm whose version has been superseded - so the moment a
   * read observes a version other than V, that arm can never commit, whatever
   * else happened. Until then the state genuinely is unknown.
   */
  const [pendingArmVersion, setPendingArmVersion] = useState<number | null>(null)
  /**
   * Whether an arm request is still executing, tracked independently of the
   * mutation.
   *
   * `mutation.variables` describes the LATEST call, so starting a disarm while
   * an arm was still in flight made the arm invisible - and with the cached
   * disarmed value the card went back to claiming "Disarmed" with only an arm
   * button, while both writes were unresolved. Which is precisely the moment
   * least safe to make that claim.
   */
  const [armInFlight, setArmInFlight] = useState(false)
  // When this instance of the card mounted. Anything read before that predates
  // the current view and is not yet confirmed for it. A lazy useState
  // initializer rather than a ref: it runs once, and reading it during render
  // is pure - a ref holding Date.now() is neither.
  const [mountedAt] = useState(() => Date.now())
  // Set to the data version in hand when a definitive rejection arrives, and
  // cleared as soon as a newer read replaces it.
  const [reconcilingFrom, setReconcilingFrom] = useState<number | null>(null)

  const {
    data: automation,
    isLoading,
    error,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['security-deposit-automation'],
    // Bounded deliberately. A server that accepts the connection and then hangs
    // would otherwise leave this query loading forever, so it could never reach
    // the error branch - and the spinner state has no Disarm action, taking the
    // emergency stop away during exactly the kind of partial incident where the
    // POST endpoint may still be perfectly healthy. Timing out turns a hang into
    // the "state unknown" state, which does offer Disarm.
    queryFn: ({ signal }) => withTimeout(s => api.getSecurityDepositAutomation(s), signal),
    // This is global state anyone (or any API client) can change, and the app's
    // QueryClient disables refetch-on-focus. Without polling, a Settings page
    // left open from before someone armed production would keep showing
    // "Disarmed" and keep offering only "Arm" - so the one operator watching
    // the screen could neither see the live risk nor reach Disarm without a
    // reload. Safety-critical state has to keep looking.
    refetchInterval: 30_000,
    // Keep polling in a hidden tab. React Query pauses interval refetches in
    // the background by default, so a card left open in an unfocused tab could
    // sit on hours-old data - and on return would render that cached
    // "Disarmed" with only an arm action while the focus refetch ran. Polling
    // through closes the gap at its source rather than papering over it: the
    // data on screen is never more than one interval old.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    // Always re-read on mount. Returning to this page inside the cache window
    // would otherwise render the previously cached value immediately, with
    // isLoading false - so if someone armed the switch while the operator was
    // away, the card would show "Disarmed" and offer only another arm.
    refetchOnMount: 'always',
    // No retry, unlike the app-wide default of 1. A retry would double the time
    // spent in the loading state - where there is no disarm action - turning a
    // 10s bounded read into ~21s before the emergency stop becomes reachable.
    // Failing fast to "state unknown" is the safer behaviour here; the 30s poll
    // is the recovery path, not a retry.
    retry: false,
  })

  const mutation = useMutation<SecurityDepositAutomation, unknown, AutomationWriteVars>({
    mutationFn: ({ enabled, reason, ifVersion }: AutomationWriteVars) =>
      withTimeout(s =>
        api.setSecurityDepositAutomation(enabled, {
          reason,
          // The version the decision was based on, captured when the operator
          // decided - never the latest rendered value. Arming without it is
          // rejected server-side, which is what stops an arm this client
          // abandoned from landing later and overtaking a disarm.
          ifVersion,
          signal: s,
        })
      ),
    onSuccess: result => {
      // Responses can arrive out of order. Both writes commit server-side in
      // the order the server accepted them, but an arm at V+1 and a disarm at
      // V+2 can come back reversed - and applying the late arm would overwrite
      // the newer cached state and announce "armed" for a switch the server has
      // already turned off. The version says which response is actually later.
      const cached = queryClient.getQueryData<SecurityDepositAutomation>([
        'security-deposit-automation',
      ])
      if (cached && result.version < cached.version) {
        // Superseded: revalidate, but neither cache it nor announce it.
        queryClient.invalidateQueries({ queryKey: ['security-deposit-automation'] })
        return
      }

      // Write the canonical response into the cache before revalidating. An
      // invalidation alone only *starts* a refetch, so on a slow network the
      // card would keep rendering the old state - offering "Arm" moments after
      // a successful arm - while the toast said it had worked.
      queryClient.setQueryData(['security-deposit-automation'], result)
      queryClient.invalidateQueries({ queryKey: ['security-deposit-automation'] })
      setConfirmOpen(false)
      setReason('')
      setPendingArmVersion(null)
      setArmVersionAtOpen(null)
      setReconcilingFrom(null)
      toast({
        title: result.enabled ? 'Deposit automation armed' : 'Deposit automation disarmed',
        description: result.enabled
          ? 'The next sweep will place real holds on guest cards.'
          : 'The next sweep will record decisions without moving money.',
      })
    },
    onMutate: (variables: { enabled: boolean }) => {
      if (variables.enabled) setArmInFlight(true)
    },
    onSettled: (_data: unknown, _err: unknown, variables: { enabled: boolean } | undefined) => {
      // Keyed off this call's own variables, not the mutation's current ones.
      if (variables?.enabled) setArmInFlight(false)
    },
    onError: (err: unknown, variables: AutomationWriteVars) => {
      // A 4xx is the server telling us definitively that nothing happened - a
      // version conflict, most often, meaning another accepted write landed
      // first. That is not the ambiguous case: there is no abandoned request
      // that might still commit, so starting the 60s uncertainty window would
      // strand the card in "unknown" with no arm action for a minute over an
      // outcome we already know. Refresh and show the canonical state instead.
      const status = (err as { status?: number } | null)?.status
      // AuthorizationError (401/403) is thrown without a `status`, so a status
      // check alone would misread the most definitive rejection there is as an
      // ambiguous write and suppress the arm action for the rest of the 60s
      // window after the operator re-authenticated.
      const isAuthError = err instanceof AuthorizationError
      const definitivelyRejected =
        isAuthError || (typeof status === 'number' && status >= 400 && status < 500)

      if (definitivelyRejected) {
        setConfirmOpen(false)
        setReason('')
        // Hold the actionable unknown state until the canonical read lands.
        // `invalidateQueries` only STARTS a refetch, so without this the cached
        // value stays on screen meanwhile - and when the 409 was caused by
        // someone else arming, that means showing "Disarmed" with only an arm
        // action while real holds are live, for as long as the bounded read
        // takes. Cleared by the read itself, not by a timer.
        setReconcilingFrom(dataUpdatedAt)
        queryClient.invalidateQueries({ queryKey: ['security-deposit-automation'] })
        toast({
          title: 'That change did not apply',
          description:
            status === 409
              ? 'The switch changed since this page last read it. The current state is shown.'
              : err instanceof Error
                ? err.message
                : 'The server rejected the change.',
          variant: 'destructive',
        })
        return
      }

      // An arm that timed out may still have been committed server-side, so the
      // cached "disarmed" state can no longer be trusted. Continuing to show it
      // would leave the operator with only an "Arm" button while real holds
      // might already be enabled, until the 30s poll caught up. Mark the state
      // uncertain - which renders the actionable unknown state, with disarm -
      // and refetch immediately rather than waiting for the poll.
      // Only an arm needs this. A disarm that lands late is harmless - it is
      // the safe direction - so there is nothing to stay uncertain about.
      // The version this request actually SENT, not whatever is rendered now. A
      // poll can land while the POST is in flight, and recording the newer value
      // would leave the card stuck in the unknown state: reads would keep
      // matching it even though the abandoned request was already resolved.
      //
      // Only an arm sets this, and a FAILED disarm must not clear it. Chaining
      // the two - arm times out, operator hits "Disarm anyway", that fails too -
      // would otherwise drop the warning while the original arm was still in
      // flight, and the card would claim disarmed again. Uncertainty ends when a
      // disarm succeeds or a read shows the version moved, not when one fails.
      if (variables.enabled) setPendingArmVersion(variables.ifVersion ?? null)
      // Close the confirm dialog too. Leaving it open kept the operator staring
      // at an "Arm" prompt, with the card - and its disarm - behind a modal,
      // which is the opposite of what a failed arm calls for.
      setConfirmOpen(false)
      setReason('')
      queryClient.invalidateQueries({ queryKey: ['security-deposit-automation'] })
      toast({
        title: 'Could not change the kill switch',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    },
  })

  const isArmed = automation?.enabled === true
  // Arming and disarming do NOT share a pending lock. A pending arm used to
  // disable every disarm button, so the emergency stop was unavailable for up
  // to the full request timeout at exactly the moment someone had just asked to
  // turn real card operations on. Ordering makes that safe to allow: a disarm
  // always applies and bumps the version, so an arm that lands afterwards
  // carries a superseded version and is declined by the server.
  //
  // Only the disarm side needs its own flag: the disarm buttons narrow to it so
  // a pending arm leaves them live. The arm paths take the broad `isBusy`, which
  // already covers a pending arm, so there is no separate arm flag.
  const isDisarmPending = mutation.isPending && mutation.variables?.enabled === false
  const isBusy = mutation.isPending
  // Uncertainty after a failed write does NOT clear on the first read that
  // follows it. Aborting the request client-side doesn't stop the server, so an
  // arm that timed out at 10s may still be in flight: the immediate re-read can
  // legitimately return the old "disarmed" value and the write commit a moment
  // later. Clearing on that read would put the card back to offering only
  // "Arm" while real holds were going live.
  //
  // So it clears on only two things:
  //   - a read that observes `enabled: true` - the ambiguity is resolved, and
  //     the card can show ARMED with a working disarm; or
  //   - enough time passing that the abandoned request cannot still be running.
  // The 30s poll supplies the re-renders, so no timer is needed here.
  // Data carried over from a previous visit, before this mount's re-read lands.
  // The polling fix only protects the page while it stays mounted; navigating
  // away and back re-renders from cache, and showing a possibly-stale
  // "Disarmed" with only an arm action is the failure this exists to prevent.
  const awaitingFirstConfirmedRead = dataUpdatedAt > 0 && dataUpdatedAt < mountedAt

  const stateUncertain =
    // An arm in flight means the state genuinely is unknown - it may already
    // have committed server-side. Saying so puts the card into its actionable
    // unknown branch, which is what actually renders a disarm during that
    // window; removing the shared pending lock only stopped disabling one that
    // was never on screen.
    armInFlight ||
    awaitingFirstConfirmedRead ||
    // Either: a definitive rejection whose canonical read hasn't landed yet...
    (reconcilingFrom !== null && dataUpdatedAt === reconcilingFrom) ||
    // ...or an abandoned arm that can still commit, i.e. one whose version the
    // server has not yet moved past.
    (pendingArmVersion !== null && automation?.version === pendingArmVersion)

  // An open arm confirmation is CANCELLED - not merely hidden - as soon as the
  // switch changes underneath it or the card stops trusting what it shows. A
  // modal left up hides the live state and "Disarm anyway" from the operator,
  // and would let them confirm an arm against state the card just disowned.
  //
  // Hiding it behind a derived `open` prop was not enough: `confirmOpen` and the
  // captured version survived, so a later successful poll reporting the same
  // version resurrected the old confirmation, reason text and all. Changing a
  // controlled dialog's `open` does not reset the state behind it.
  //
  // Cleared during render rather than from an effect - React's documented
  // "adjust state when something changes" pattern. It converges in one pass,
  // because the condition is false immediately after.
  const confirmationStale =
    confirmOpen &&
    (Boolean(error) ||
      stateUncertain ||
      (armVersionAtOpen !== null && automation?.version !== armVersionAtOpen))

  if (confirmationStale) {
    setConfirmOpen(false)
    setArmVersionAtOpen(null)
    setReason('')
  }

  return (
    <Card className={isArmed ? 'border-danger/40' : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {/* The reassuring shield is reserved for a state we actually know.
              While loading, errored or uncertain, `isArmed` is false simply
              because there is nothing trustworthy to read - rendering "safe"
              there is a confident claim the card is in no position to make. */}
          {isArmed ? (
            <ShieldAlert className="h-5 w-5 text-danger" />
          ) : isLoading || error || stateUncertain ? (
            <ShieldQuestion className="h-5 w-5 text-textMuted" />
          ) : (
            <ShieldCheck className="h-5 w-5 text-textMuted" />
          )}
          Security deposit automation
        </CardTitle>
        <CardDescription>
          The master kill switch for Stripe deposit holds on every booking except Airbnb. When
          disarmed, the hourly sweep still runs and records what it would have done, but never moves
          money.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-textMuted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading current state...
          </p>
        ) : error || stateUncertain ? (
          // A failed read must not take the emergency stop away with it. The
          // GET can fail while POST is perfectly healthy, and disarming is safe
          // and idempotent - disarming an already-disarmed switch does nothing.
          // So the state is reported as unknown, and Disarm stays available.
          // Arm does NOT: arming while the real state is unknown is exactly the
          // decision nobody should be making blind.
          <div className="space-y-3">
            <p className="text-sm text-danger">
              {error
                ? `Could not read the kill switch, so the current state is unknown: ${
                    error instanceof Error ? error.message : 'Unknown error'
                  }`
                : 'That change did not complete, so the current state is unknown: it may or may not have been applied.'}
            </p>
            <Button
              variant="destructive"
              className="w-fit"
              disabled={isDisarmPending}
              onClick={() =>
                mutation.mutate({ enabled: false, reason: 'disarmed while state unreadable' })
              }
            >
              {isDisarmPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disarming...
                </>
              ) : (
                'Disarm anyway'
              )}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  isArmed ? 'bg-danger/10 text-danger' : 'bg-surfaceMuted text-textMuted'
                }`}
              >
                {isArmed ? 'ARMED - real holds' : 'Disarmed - dry run'}
              </span>
              {/* Which value is actually in force: an explicit decision someone
                  made, or the untouched default. Worth stating rather than
                  leaving an operator to infer it during an incident. */}
              <span className="text-xs text-textMuted">
                {automation?.source === 'runtime'
                  ? 'set explicitly'
                  : 'never set, using the environment default'}
              </span>
            </div>

            {automation?.source === 'runtime' && (
              <dl className="grid gap-1 text-xs text-textMuted">
                {automation.updated_by && (
                  <div className="flex gap-2">
                    <dt className="font-medium">Changed by</dt>
                    <dd>{automation.updated_by}</dd>
                  </div>
                )}
                {automation.updated_at && (
                  <div className="flex gap-2">
                    <dt className="font-medium">When</dt>
                    <dd>{new Date(automation.updated_at).toLocaleString()}</dd>
                  </div>
                )}
                {automation.reason && (
                  <div className="flex gap-2">
                    <dt className="font-medium">Reason</dt>
                    <dd>{automation.reason}</dd>
                  </div>
                )}
              </dl>
            )}

            {isArmed ? (
              // One click, no dialog: this is the emergency stop.
              <Button
                variant="destructive"
                className="w-fit"
                disabled={isDisarmPending}
                onClick={() =>
                  mutation.mutate({ enabled: false, reason: 'disarmed from settings' })
                }
              >
                {isDisarmPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Disarming...
                  </>
                ) : (
                  'Disarm now'
                )}
              </Button>
            ) : (
              <Button
                className="w-fit"
                disabled={isBusy}
                onClick={() => {
                  setArmVersionAtOpen(automation?.version ?? null)
                  setConfirmOpen(true)
                }}
              >
                Arm automation
              </Button>
            )}
          </>
        )}
      </CardContent>

      <Dialog
        open={confirmOpen}
        onOpenChange={next => {
          // Dismissing clears the draft reason. Leaving it prefilled meant a
          // reason typed for an abandoned decision could be submitted as a
          // later attempt's audit reason - misleading history on the record
          // that says why real card operations were switched on.
          if (!next) {
            setArmVersionAtOpen(null)
            setReason('')
          }
          setConfirmOpen(next)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <AlertTriangle className="h-5 w-5" />
              Arm deposit automation?
            </DialogTitle>
            <DialogDescription>
              The next hourly sweep will place real authorization holds on guests&apos; cards, for
              every booking except Airbnb, 24 hours before arrival. It never places a first hold on
              a guest who has already checked out, and it takes over any hold Uplisting still has
              open rather than adding a second one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label htmlFor="arm-reason" className="text-sm font-medium">
              Reason (recorded in the audit trail)
            </label>
            <Input
              id="arm-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="planned cutover"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setArmVersionAtOpen(null)
                setReason('')
                setConfirmOpen(false)
              }}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isBusy}
              onClick={() =>
                mutation.mutate({
                  enabled: true,
                  reason: reason.trim() || undefined,
                  // The version the dialog opened on, not the latest poll's.
                  ifVersion: armVersionAtOpen ?? undefined,
                })
              }
            >
              {isBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Arming...
                </>
              ) : (
                'Arm automation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
